/**
 * ErrorBoundary global — catch les erreurs render/lifecycle.
 *
 * Limite : ne catch PAS les erreurs async (fetch, promesses non-gérées).
 * Pour celles-là, prévoir un global `window.addEventListener('unhandledrejection')`
 * dans un chunk dédié plus tard.
 *
 * i18n : on appelle directement l'instance i18next globale (pas de hook
 * possible dans une class, et `withTranslation` posait un souci TS2742 de
 * portabilité de type avec composite/declaration). `i18n.t()` est synchro
 * et lit la langue active du store.
 */

import i18n from "i18next";
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const t = i18n.t.bind(i18n);

    return (
      <div className="grid min-h-screen place-items-center bg-[color:var(--brand-bg)] p-6">
        <div className="card max-w-md text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-red-100 text-2xl text-red-700">
            !
          </div>
          <h1 className="mt-4 text-xl font-semibold text-[color:var(--brand-text)]">
            {t("errors.boundary.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("errors.boundary.subtitle")}</p>
          <button type="button" onClick={this.handleReload} className="btn-primary mt-6">
            {t("errors.boundary.reload")}
          </button>
        </div>
      </div>
    );
  }
}
